/**
 * ToggleButton - Floating Action Button with ReadingVox icon
 * Shows/hides the floating panel. Positioned in a configurable corner.
 */

import type { TogglePosition } from '../types/config';
import type { ResolvedTheme } from './ThemeManager';
import { ensureFocusStyles } from './a11y';
import { t } from '../i18n';

export interface ToggleButtonOptions {
  position: TogglePosition;
  theme: ResolvedTheme;
  onToggle: (visible: boolean) => void;
  /** Pulse + "Listen" label to draw first-time visitors' attention (default true) */
  pulse?: boolean;
  /** Distance (px) of a bottom-* button from the viewport bottom (default 72) */
  bottomOffset?: number;
}

const BRAND_GREEN = '#2ec486';
const SEEN_KEY = 'rv-toggle-seen';
const STYLE_ID = 'rv-toggle-pulse-style';
const REPEAT_MS = 15000;

const PULSE_CSS = ` @keyframes rv-toggle-pulse { 0%   { transform: scale(1);   opacity: 0.55; } 80%  { transform: scale(1.9); opacity: 0; } 100% { transform: scale(1.9); opacity: 0; } } .rv-toggle-ring { position: absolute; inset: -2px; border-radius: 50%; background: ${BRAND_GREEN}; pointer-events: none; opacity: 0; z-index: -1; } .rv-toggle-ring.rv-pulsing { animation: rv-toggle-pulse 1.8s ease-out var(--rv-pulses, 3); } .rv-toggle-label { position: absolute; top: 50%; white-space: nowrap; pointer-events: none; padding: 6px 12px; border-radius: 999px; font: 600 13px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; background: #0f172a; color: #fff; box-shadow: 0 4px 14px rgba(0,0,0,0.18); opacity: 0; transform: translateY(-50%) translateX(var(--rv-label-shift, 6px)); transition: opacity 0.35s ease, transform 0.35s ease; } .rv-toggle-label.rv-visible { opacity: 1; transform: translateY(-50%) translateX(0); } @media (prefers-reduced-motion: reduce) { .rv-toggle-ring.rv-pulsing { animation: none; } .rv-toggle-label { transition: none; } } `;

// ReadingVox icon — 5-bar audio waveform matching the actual icon geometry
const ICON_SVG = `<svg viewBox="26 57 167 109" width="24" height="24" xmlns="http://www.w3.org/2000/svg"> <rect x="31" y="90" width="26" height="45" rx="13" fill="#2ec486"/> <rect x="63" y="65" width="26" height="95" rx="13" fill="#8d99ae"/> <rect x="96" y="82" width="26" height="61" rx="13" fill="#2ec486"/> <rect x="129" y="65" width="26" height="95" rx="13" fill="#ff9f1c"/> <rect x="161" y="90" width="26" height="45" rx="13" fill="#8d99ae"/> </svg>`;

export class ToggleButton {
  private element: HTMLButtonElement;
  private options: ToggleButtonOptions;
  private isActive: boolean = false;
  private ring: HTMLSpanElement | null = null;
  private label: HTMLSpanElement | null = null;
  private repeatTimer: number | null = null;
  private labelTimer: number | null = null;

  constructor(options: ToggleButtonOptions) {
    this.options = options;
    this.element = this.createElement();
    document.body.appendChild(this.element);
    if (options.pulse !== false && !this.hasBeenSeen()) this.startAttention();
  }

  // --- Attention pulse (first-time visitors only) ---

  private hasBeenSeen(): boolean {
    try { return localStorage.getItem(SEEN_KEY) === '1'; } catch { return false; }
  }

  private markSeen(): void {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* storage blocked */ }
  }

  private startAttention(): void {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = PULSE_CSS;
      document.head.appendChild(style);
    }

    this.ring = document.createElement('span');
    this.ring.className = 'rv-toggle-ring';
    this.element.prepend(this.ring);

    // "Listen to this page" label on the side facing into the page
    const onLeft = this.options.position.endsWith('left');
    this.label = document.createElement('span');
    this.label.className = 'rv-toggle-label';
    this.label.textContent = t('Listen to this page');
    this.label.style.cssText = onLeft
      ? 'left: calc(100% + 10px); --rv-label-shift: -6px;'
      : 'right: calc(100% + 10px); --rv-label-shift: 6px;';
    this.element.appendChild(this.label);

    // Let the page settle, then pulse 3 times with the label; repeat a short burst until clicked
    window.setTimeout(() => {
      this.pulse(3);
      this.label?.classList.add('rv-visible');
      this.labelTimer = window.setTimeout(() => this.label?.classList.remove('rv-visible'), 5000);
    }, 1200);
    this.repeatTimer = window.setInterval(() => this.pulse(2), REPEAT_MS);
  }

  private pulse(times: number): void {
    if (!this.ring || this.isActive) return;
    this.ring.classList.remove('rv-pulsing');
    void this.ring.offsetWidth; // restart the animation
    this.ring.style.setProperty('--rv-pulses', String(times));
    this.ring.classList.add('rv-pulsing');
  }

  private stopAttention(): void {
    if (this.repeatTimer !== null) window.clearInterval(this.repeatTimer);
    if (this.labelTimer !== null) window.clearTimeout(this.labelTimer);
    this.repeatTimer = this.labelTimer = null;
    this.ring?.remove();
    this.label?.remove();
    this.ring = this.label = null;
  }

  private createElement(): HTMLButtonElement {
    const btn = document.createElement('button');
    ensureFocusStyles();
    btn.className = 'rv-toggle-btn';
    btn.setAttribute('aria-label', t('ReadingVox reading toolbar'));
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('title', 'ReadingVox');

    const { theme, position } = this.options;

    // Position offsets
    const posStyle = this.getPositionStyle(position);

    btn.style.cssText = ` position: fixed; ${posStyle} width: 48px; height: 48px; border-radius: 50%; border: 2px solid ${theme.borderColor}; background: ${theme.backgroundColor}; cursor: pointer; z-index: 2147483646; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 12px rgba(0,0,0,0.15); transition: box-shadow 0.2s, transform 0.2s; padding: 0; isolation: isolate; `;

    btn.innerHTML = ICON_SVG;

    btn.addEventListener('click', () => {
      if (this.ring) {
        this.stopAttention();
        this.markSeen();
      }
      this.isActive = !this.isActive;
      this.updateActiveState();
      this.options.onToggle(this.isActive);
    });

    btn.addEventListener('mouseenter', () => {
      btn.style.transform = 'scale(1.08)';
      btn.style.boxShadow = `0 4px 16px rgba(0,0,0,0.2)`;
    });

    btn.addEventListener('mouseleave', () => {
      btn.style.transform = 'scale(1)';
      this.updateActiveState();
    });

    return btn;
  }

  private getPositionStyle(pos: TogglePosition): string {
    const offset = '16px';
    const b = this.options.bottomOffset;
    const bottom = typeof b === 'number' && b >= 0 ? `${Math.round(b)}px` : '72px';
    switch (pos) {
      case 'top-left':    return `top: ${offset}; left: ${offset};`;
      case 'top-right':   return `top: ${offset}; right: ${offset};`;
      case 'bottom-left': return `bottom: ${bottom}; left: ${offset};`;
      case 'bottom-right':
      default:            return `bottom: ${bottom}; right: ${offset};`;
    }
  }

  private updateActiveState(): void {
    const { theme } = this.options;
    this.element?.setAttribute('aria-expanded', String(this.isActive));
    if (this.isActive) {
      this.element.style.boxShadow = `0 0 0 3px ${theme.primaryColor}40, 0 2px 12px rgba(0,0,0,0.15)`;
      this.element.style.borderColor = theme.primaryColor;
    } else {
      this.element.style.boxShadow = '0 2px 12px rgba(0,0,0,0.15)';
      this.element.style.borderColor = this.options.theme.borderColor;
    }
  }

  setActive(active: boolean): void {
    this.isActive = active;
    this.updateActiveState();
  }

  show(): void {
    this.element.style.display = 'flex';
  }

  hide(): void {
    this.element.style.display = 'none';
  }

  destroy(): void {
    this.stopAttention();
    this.element.remove();
  }
}
